import { useTreeViewContext } from '@aibees/elephant/context/tree-view/context';
import { Nil } from '@aibees/shared/definition';
import { TreeViewBaseItem } from '@mui/x-tree-view';
// eslint-disable-next-line no-restricted-imports
import { UseTreeViewItemsPublicAPI } from '@mui/x-tree-view/internals/plugins/useTreeViewItems/useTreeViewItems.types';
import { uniq } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';
import { TreeItemData } from './types';
import { getItemChildValues, getItemParentValues } from './utils';

export function useHandleSelection(
  getApi: () => Nil<UseTreeViewItemsPublicAPI<TreeViewBaseItem<TreeItemData>>>,
  onChange?: (selectedItems: string[], selectedValue?: string) => void
) {
  const { selectedValues, setSelectedValues } = useTreeViewContext();

  const selectedValuesRef = useRef(selectedValues);
  selectedValuesRef.current = selectedValues;

  const handleSelection = useCallback(
    (
      checked: boolean,
      indeterminate: boolean,
      itemId: string,
      itemValue: string
    ) => {
      const api = getApi();
      if (api) {
        const currentSelected = selectedValuesRef.current;
        const item = api.getItem(itemId) as TreeViewBaseItem<TreeItemData>;
        const parentValues = getItemParentValues(item, api);

        if (checked || indeterminate) {
          const childrenValues = item.children?.map((i) => i.value) ?? [];
          const allChildrenValues = getItemChildValues(item);
          const valuesToSelect = item.doNotSetValueOnSelection
            ? childrenValues
            : [itemValue];

          let valuesToUnselect =
            indeterminate && !item.doNotSetValueOnSelection
              ? allChildrenValues
              : allChildrenValues.filter((i) => !childrenValues.includes(i));

          if (item.parentId) {
            const parent = api.getItem(
              item.parentId
            ) as TreeViewBaseItem<TreeItemData>;

            if (!parent.doNotSetValueOnSelection) {
              const allSiblingValues =
                parent.children?.map((i) => i.value) ?? [];

              if (
                allSiblingValues.every((i) =>
                  [...currentSelected, itemValue].includes(i)
                )
              ) {
                valuesToSelect.push(parent.value);
                valuesToUnselect = valuesToUnselect.concat(allSiblingValues);
              }
            }
          }

          setSelectedValues((prev) => {
            const newItems = uniq([...prev, ...valuesToSelect]).filter(
              (p) => !valuesToUnselect.includes(p)
            );
            onChange?.(newItems, itemValue);
            return newItems;
          });
        } else if (
          item.parentId &&
          parentValues.some((parentValue) =>
            currentSelected.includes(parentValue)
          )
        ) {
          const valuesToUnselect = [item.value].concat(parentValues);

          let valuesToSelect: string[] = [];

          const parent = api.getItem(
            item.parentId
          ) as TreeViewBaseItem<TreeItemData>;
          valuesToSelect = valuesToSelect.concat(
            parent.children?.map((i) => i.value) ?? []
          );

          setSelectedValues((prev) => {
            const newItems = uniq([...prev, ...valuesToSelect]).filter(
              (p) => !valuesToUnselect.includes(p)
            );
            onChange?.(newItems);
            return newItems;
          });
        } else {
          const parentValuesDraft = getItemParentValues(item, api);
          const valuesToUnselect = [itemValue].concat(parentValuesDraft);

          valuesToUnselect.push(...getItemChildValues(item));

          setSelectedValues((prev) => {
            const newItems = prev.filter((p) => !valuesToUnselect.includes(p));
            onChange?.(newItems);
            return newItems;
          });
        }
      }
    },
    [getApi, onChange, setSelectedValues]
  );

  return useMemo(() => ({ handleSelection }), [handleSelection]);
}
